import { useState, useEffect } from 'react';
import { Mail, Plus, Edit, Trash2, Inbox, Info } from 'lucide-react';
import clientAuthService from '../services/clientAuthService.js';

const ClientRecipientsManagement = () => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    isPrimary: false,
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    try {
      setLoading(true);
      // const response = await fetch('/api/client-recipients.php', {
      //   headers: {
      //     'Content-Type': 'application/json',
      //     ...ClientAuthService.getAuthHeaders()
      //   }
      // });
      const response = await clientAuthService.getEmailRecipients();
      if (response.data) {
        setRecipients(response.data);
      } else {
        setRecipients([]);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSaving(true);

      // Add clientId to formData
      const clientId = editingRecipient ? editingRecipient.clientId : (recipients[0] ? recipients[0].clientId : null);
      const formDataWithClient = { ...formData, clientId };
      
      const response = editingRecipient 
        ? await clientAuthService.updateEmailRecipient(editingRecipient._id, formDataWithClient)
        : await clientAuthService.addEmailRecipient(formDataWithClient);

      if (response.data) {
        await fetchRecipients();
        cancelForm();
        // Show success message could be added here
      } else {
        // const result = await response.json();
        alert('Failed to save recipient');
      }
    } catch (error) {
      console.error('Error saving recipient:', error);
      alert('An error occurred while saving the recipient');
    } finally {
      setSaving(false);
    }
  };

  const deleteRecipient = async (recipient) => {
    if (recipients.length <= 1) {
      alert('You must have at least one recipient.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${recipient.name}?`)) {
      return;
    }

    try {
      const response = await clientAuthService.deleteEmailRecipient(recipient._id);

      if (response.success) {
        await fetchRecipients();
      } else {
        alert('Failed to delete recipient');
      }
    } catch (error) {
      console.error('Error deleting recipient:', error);
      alert('An error occurred while deleting the recipient');
    }
  };

  const startEdit = (recipient) => {
    setEditingRecipient(recipient);
    setFormData({
      name: recipient.name,
      email: recipient.email,
      isPrimary: Boolean(recipient.isPrimary),
      isActive: Boolean(recipient.isActive)
    });
    setShowAddForm(true);
    setErrors({});
  };

  const startAdd = () => {
    setEditingRecipient(null);
    setFormData({
      name: '',
      email: '',
      isPrimary: false,
      isActive: true
    });
    setShowAddForm(true);
    setErrors({});
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingRecipient(null);
    setFormData({
      name: '',
      email: '',
      isPrimary: false,
      isActive: true
    });
    setErrors({});
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h5 className="text-lg font-semibold text-gray-900 mb-0 flex items-center">
          <Mail size={16} className="mr-2" />
          Email Recipients
        </h5>
        <button 
          type="button" 
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-3 rounded-md text-sm transition duration-200 flex items-center"
          onClick={startAdd}
        >
          <Plus size={16} className="mr-1" />
          Add Recipient
        </button>
      </div>

      <div className="p-6">
        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg border-2 border-blue-200 shadow-sm mb-6">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h6 className="text-base font-semibold text-gray-900 mb-0">
                {editingRecipient ? 'Edit Recipient' : 'Add New Recipient'}
              </h6>
            </div>
            <div className="p-4">
              <div onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.name ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'
                        }`}
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                      {errors.name && <div className="text-red-600 text-sm mt-1">{errors.name}</div>}
                    </div>
                  </div>
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.email ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'
                        }`}
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                      {errors.email && <div className="text-red-600 text-sm mt-1">{errors.email}</div>}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="mb-4">
                      <div className="flex items-start">
                        <input
                          className="h-4 w-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mr-3 mt-1"
                          type="checkbox"
                          id="isPrimary"
                          checked={formData.isPrimary}
                          onChange={(e) => setFormData({...formData, isPrimary: e.target.checked})}
                        />
                        <div>
                          <label className="text-sm font-medium text-gray-700" htmlFor="isPrimary">
                            Make this the primary recipient
                          </label>
                          <div className="text-sm text-gray-500 mt-1">
                            Primary recipients are the main contact for this client.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="mb-4">
                      <div className="flex items-start">
                        <input
                          className="h-4 w-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mr-3 mt-1"
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        />
                        <div>
                          <label className="text-sm font-medium text-gray-700" htmlFor="isActive">
                            Active recipient
                          </label>
                          <div className="text-sm text-gray-500 mt-1">
                            Only active recipients receive emails.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button 
                    type="button" 
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={saving}
                    onClick={handleSubmit}
                  >
                    {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                    {editingRecipient ? 'Update' : 'Add'} Recipient
                  </button>
                  <button 
                    type="button" 
                    className="border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2 px-4 rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={cancelForm}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recipients List */}
        {recipients.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Inbox size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-lg mb-2">No email recipients configured.</p>
            <p className="text-sm">Add recipients to specify who should receive invoices and quotes.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recipients.map(recipient => (
                  <tr key={recipient._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">{recipient.name}</span>
                        {recipient.isPrimary && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Primary
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <a 
                        href={`mailto:${recipient.email}`} 
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {recipient.email}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        recipient.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {recipient.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition duration-200"
                          onClick={() => startEdit(recipient)}
                          title="Edit recipient"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => deleteRecipient(recipient)}
                          title="Delete recipient"
                          disabled={recipients.length <= 1}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {recipients.length <= 1 && recipients.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md mt-4 flex items-start">
            <Info size={16} className="mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Note:</strong> You must have at least one email recipient. This ensures you always receive important documents like invoices and quotes.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientRecipientsManagement;